// Phase 9 — per-project session sidebar.
//
// Replaces the global `HistorySidebar`. The list now comes from the
// Firestore-backed `sessions_index` (with a legacy fallback when the
// store isn't wired). Each row carries title, last-active timestamp,
// mode badges, an MR badge, and a three-dot menu (Rename / Delete).

import { useEffect, useRef, useState } from "react";

import { useRelativeTime } from "../i18n/format.js";
import { useT } from "../i18n/index.js";
import { AutoGrowTextarea } from "../ui/AutoGrowTextarea";
import { BADGE_TONE, MENU_ITEM, MENU_PANEL } from "../ui/classes";
import { Spinner } from "../ui/Spinner";

import {
  deleteSession,
  renameSession,
  useSessions,
  type SessionIndexEntry,
} from "./useSessions";

interface SessionSidebarProps {
  readonly projectId?: number;
  readonly activeSessionId?: string;
  readonly onPick: (sessionId: string) => void;
  readonly onNew: () => void;
}

interface RowProps {
  readonly entry: SessionIndexEntry;
  readonly active: boolean;
  readonly onPick: () => void;
  readonly onRename: (next: string) => void;
  readonly onDelete: () => void;
}

const Row = ({ entry, active, onPick, onRename, onDelete }: RowProps) => {
  const t = useT();
  const rel = useRelativeTime();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.title);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  if (editing) {
    return (
      <li>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = draft.trim();
            if (trimmed.length === 0) {
              setEditing(false);
              return;
            }
            onRename(trimmed);
            setEditing(false);
          }}
          className="flex w-full gap-1 px-2 py-1"
        >
          <AutoGrowTextarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setDraft(entry.title);
                setEditing(false);
              } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                const trimmed = draft.trim();
                if (trimmed.length === 0) {
                  setEditing(false);
                  return;
                }
                onRename(trimmed);
                setEditing(false);
              }
            }}
            aria-label={t("projects.sessionTitleAriaLabel")}
            className="flex-1 rounded border border-primary-line px-1 py-0.5 text-xs focus:border-primary focus:outline-none"
          />
        </form>
      </li>
    );
  }

  return (
    <li className="group relative">
      <button
        type="button"
        onClick={onPick}
        className={`w-full rounded-md px-2 py-1.5 text-left text-xs hover:bg-surface-3 ${
          active ? "bg-surface-3 font-medium" : ""
        }`}
      >
        <div className="flex items-center gap-1">
          <span className="truncate text-ink-3">{entry.title}</span>
          {entry.live && (
            <span
              className={`rounded-full px-1.5 text-[10px] font-medium ${BADGE_TONE.success}`}
              data-testid="session-live-badge"
            >
              {t("projects.liveBadge")}
            </span>
          )}
          {entry.hasOpenMr && (
            <span
              className={`rounded-full px-1.5 text-[10px] font-medium ${BADGE_TONE.info}`}
              data-testid="session-mr-badge"
            >
              MR
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-ink-subtle">
          <span>{rel(entry.lastActiveAt)}</span>
          {entry.modesUsed.includes("coding") && (
            <span className={`rounded px-1 text-[10px] ${BADGE_TONE.primary}`}>code</span>
          )}
          {entry.modesUsed.includes("pm") && (
            <span className="rounded bg-surface-4 px-1 text-[10px] text-ink-3">pm</span>
          )}
        </div>
      </button>
      <div ref={menuRef} className="absolute right-1 top-1">
        <button
          type="button"
          aria-label={t("projects.sessionOptionsAriaLabel")}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          className="rounded p-1 text-ink-faint opacity-0 hover:bg-surface-4 hover:text-ink-3 group-hover:opacity-100"
        >
          ⋯
        </button>
        {menuOpen && (
          <div className={`absolute right-0 w-32 ${MENU_PANEL}`}>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setDraft(entry.title);
                setEditing(true);
              }}
              className={`${MENU_ITEM} text-xs`}
            >
              {t("projects.rename")}
            </button>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onDelete();
              }}
              className="block w-full px-3 py-1 text-left text-xs text-danger-fg-bright hover:bg-danger-soft"
            >
              {t("projects.delete")}
            </button>
          </div>
        )}
      </div>
    </li>
  );
};

export function SessionSidebar({
  projectId,
  activeSessionId,
  onPick,
  onNew,
}: SessionSidebarProps) {
  const t = useT();
  const [refreshKey, setRefreshKey] = useState(0);
  const { state } = useSessions({
    ...(projectId !== undefined ? { projectId } : {}),
    refreshKey,
  });

  const handleRename = async (sessionId: string, title: string) => {
    try {
      await renameSession(sessionId, title);
      setRefreshKey((k) => k + 1);
    } catch {
      // Stay silent in v1 — a 503 (index store disabled) means the rename
      // surface isn't available in this env. The sidebar still works.
    }
  };

  const handleDelete = async (sessionId: string) => {
    try {
      await deleteSession(sessionId);
      setRefreshKey((k) => k + 1);
    } catch {
      // Best-effort — DELETE is idempotent server-side.
    }
  };

  return (
    <aside
      className="w-64 shrink-0 border-r border-line bg-surface p-3"
      data-testid="session-sidebar"
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          {projectId !== undefined ? t("projects.thisProject") : t("projects.recentSessions")}
        </div>
        <button
          type="button"
          onClick={onNew}
          className="rounded border border-line-strong px-2 py-0.5 text-[11px] text-ink-3 hover:bg-surface-3"
          data-testid="new-session-button"
        >
          {t("projects.newSession")}
        </button>
      </div>
      {state.kind === "loading" && <Spinner label={t("common.loading")} />}
      {state.kind === "error" && (
        <p className="text-xs text-danger-fg-bright">{t("projects.loadSessionsFailed", { error: state.message })}</p>
      )}
      {state.kind === "ready" && state.sessions.length === 0 && (
        <p className="text-xs text-ink-subtle">{t("projects.noSessionsYet")}</p>
      )}
      {state.kind === "ready" && (
        <ul className="space-y-0.5" data-testid="session-list">
          {state.sessions.map((s) => (
            <Row
              key={s.sessionId}
              entry={s}
              active={s.sessionId === activeSessionId}
              onPick={() => onPick(s.sessionId)}
              onRename={(next) => void handleRename(s.sessionId, next)}
              onDelete={() => void handleDelete(s.sessionId)}
            />
          ))}
        </ul>
      )}
    </aside>
  );
}
